/**
 * The Capsule studio's autosave: the shared studio autosave, told how to build
 * and store a *capsule*.
 *
 * The rules — the debounce, the flush on leaving and on backgrounding, and the two
 * that keep a session from destroying the copy it is about to offer back — live in
 * `#/components/studio/useStudioAutosave`, because the FlashBook studio needs
 * exactly the same behaviour and two copies of rules this subtle would be two that
 * drift. What is left here is the part that is actually about capsules: the
 * snapshot's shape, and the slot it goes in.
 */
import {
  type CapsuleRecoveredDraft,
  clearCapsuleSnapshot,
  createCapsuleSnapshot,
  putCapsuleSnapshot,
} from '#/lib/capsuleRecovery'
import {
  type AutosaveState,
  type StudioAutosave,
  useStudioAutosave,
} from '#/components/studio/useStudioAutosave'

export type {AutosaveState}

export type CapsuleAutosave = StudioAutosave

export function useCapsuleAutosave({
  signature,
  keep,
  draftUri,
  take,
  delayMs,
}: {
  /** The capsule's content fingerprint — the thing that changes when it changes. */
  signature: string
  /** Whether this session has work worth keeping (see the shared hook). */
  keep: boolean
  /** The draft record this work came from, so a copy can be given back later. */
  draftUri: string | null
  /** Reads the capsule, called at write time. */
  take: () => CapsuleRecoveredDraft
  delayMs?: number
}): CapsuleAutosave {
  return useStudioAutosave({
    signature,
    keep,
    take: savedAt =>
      createCapsuleSnapshot({
        draftUri,
        signature,
        capsule: take(),
        now: savedAt,
      }),
    write: putCapsuleSnapshot,
    clear: clearCapsuleSnapshot,
    delayMs,
  })
}
